import type { ComponentType, ReactNode, SVGProps } from "react";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import { EMPTY_STATE_SURFACE_CLASS_NAME } from "./empty-state-styles";
import type { SlotClassNames } from "./types";

const emptyState = tv({
  slots: {
    root: "flex flex-col items-center justify-center gap-4 p-8",
    iconWrapper: "text-text-secondary",
    content: "flex flex-col items-center gap-4 text-center",
    title: "text-lg font-semibold text-text-primary",
    description: "text-sm text-text-secondary",
  },
  variants: {
    variant: {
      default: { root: EMPTY_STATE_SURFACE_CLASS_NAME },
      content: { root: "w-full" },
    },
  },
});

interface EmptyStateProps {
  readonly icon?: ComponentType<SVGProps<SVGSVGElement>>;
  readonly iconSize?: number;
  readonly variant?: "default" | "content";
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly className?: string;
  readonly classNames?: SlotClassNames<ReturnType<typeof emptyState>>;
}

export function EmptyState({
  icon: Icon,
  iconSize = 24,
  variant = "default",
  title,
  description,
  action,
  className,
  classNames,
}: EmptyStateProps) {
  const {
    root,
    iconWrapper,
    content,
    title: titleClass,
    description: descClass,
  } = emptyState({ variant });

  return (
    <div className={root({ className: twMerge(className, classNames?.root) })}>
      {Icon && (
        <div className={iconWrapper({ className: classNames?.iconWrapper })}>
          <Icon width={iconSize} height={iconSize} />
        </div>
      )}

      <div className={content({ className: classNames?.content })}>
        <p className={titleClass({ className: classNames?.title })}>{title}</p>

        {description && (
          <p className={descClass({ className: classNames?.description })}>
            {description}
          </p>
        )}
      </div>

      {action}
    </div>
  );
}
